import { Injectable } from '@angular/core';
import { RadioState } from '@app/core/states/radio.state';
import { BREVO_EVENT } from '@app/shared/modules/brevo-events';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { combineLatest, Observable } from 'rxjs';
import { catchError, filter, mergeMap, take } from 'rxjs/operators';
import { BrevoService } from '../../../../shared/common-services/src/lib/services/brevo.service';

import {
  BreakDetails,
  BreakList2,
  BreakListBreak,
  BreakListHoursOccurrences,
} from '../models/break.model';
import { BreakService } from '../services/break.service';

import { DatesRange } from '@radioking/planning';
import moment from 'moment-timezone';
import {
  BreaksRendered,
  CreateBreakFailure,
  CreateBreakRequest,
  CreateBreakSuccess,
  DeleteBreakFailure,
  DeleteBreakRequest,
  DeleteBreakSuccess,
  DeleteSelectedBreaksFailure,
  DeleteSelectedBreaksRequest,
  DeleteSelectedBreaksSuccess,
  EditBreakFailure,
  EditBreakRequest,
  EditBreakSuccess,
  GetBreakFailure,
  GetBreakListFailure,
  GetBreakListRequest,
  GetBreakListSuccess,
  GetBreakRequest,
  GetBreakSuccess,
  ResetBreak,
  ResetBreakState,
  SelectBreaks,
  SetCurrentDay,
} from './break.actions';

export class BreakStateModel {
  breaks: BreakList2;
  selectedBreaks: number[];
  currentBreak: BreakDetails;
  currentDatesRange: DatesRange;
  currentDay: moment.Moment;
  isFetchingBreakList: boolean;
  isRenderingBreakList: boolean;
  isFetchingBreak: boolean;
  isDeletingBreak: boolean;
}

const defaultState: BreakStateModel = {
  breaks: null,
  selectedBreaks: [],
  currentBreak: null,
  currentDatesRange: null,
  currentDay: moment(),
  isFetchingBreakList: true,
  isRenderingBreakList: false,
  isFetchingBreak: false,
  isDeletingBreak: false,
};

@State<BreakStateModel>({
  name: 'break',
  defaults: defaultState,
})
@Injectable()
export class BreakState {
  constructor(
    private readonly store: Store,
    private readonly breakService: BreakService,
    private readonly brevoService: BrevoService,
  ) {}

  @Selector()
  static hoursOccurrences(state: BreakStateModel): BreakListHoursOccurrences[] {
    return state.breaks.occurrences;
  }

  @Selector()
  static breaks(state: BreakStateModel): BreakListBreak[] {
    return state.breaks.breaks;
  }

  @Selector()
  static selectedBreaksCount(state: BreakStateModel): number {
    return state.selectedBreaks.length;
  }

  @Selector()
  static currentBreak(state: BreakStateModel): BreakDetails {
    return state.currentBreak;
  }

  @Selector()
  static isFetchingBreakList(state: BreakStateModel): boolean {
    return state.isFetchingBreakList;
  }

  @Selector()
  static isRenderingBreakList(state: BreakStateModel): boolean {
    return state.isRenderingBreakList;
  }

  @Selector()
  static isFetchingBreak(state: BreakStateModel): boolean {
    return state.isFetchingBreak;
  }

  @Selector()
  static isDeletingBreak(state: BreakStateModel): boolean {
    return state.isDeletingBreak;
  }

  @Selector()
  static datesRange(state: BreakStateModel): DatesRange {
    return state.currentDatesRange;
  }

  @Selector()
  static currentDay(state: BreakStateModel): moment.Moment {
    return state.currentDay;
  }

  @Action(GetBreakListRequest)
  getBreakList(ctx: StateContext<BreakStateModel>, { range }: GetBreakListRequest) {
    ctx.patchState({
      currentDatesRange: range,
      isFetchingBreakList: true,
      isRenderingBreakList: true,
    });
    return combineLatest([this.getCurrentRadioId(), this.getCurrentRadioTimezone()]).pipe(
      mergeMap(([id, tz]) => {
        return this.breakService.getBreakList(id, tz, range.from, range.to);
      }),
      mergeMap(breaks => {
        ctx.setState(
          patch<BreakStateModel>({
            breaks,
          }),
        );
        return ctx.dispatch(new GetBreakListSuccess());
      }),
      catchError(err => ctx.dispatch(new GetBreakListFailure(err))),
    );
  }

  @Action([GetBreakListSuccess, GetBreakListFailure])
  getBreakListDone(ctx: StateContext<BreakStateModel>) {
    ctx.patchState({ isFetchingBreakList: false, selectedBreaks: [] });
  }

  /**
   * Needed for the case of no breaks, the request throw an error
   */
  @Action(GetBreakListFailure)
  getBreakListFailure(ctx: StateContext<BreakStateModel>) {
    ctx.patchState({ isRenderingBreakList: false, breaks: null });
  }

  @Action(GetBreakRequest)
  getBreak(ctx: StateContext<BreakStateModel>, { breakId }: GetBreakRequest) {
    ctx.patchState({ isFetchingBreak: true });
    return this.getCurrentRadioId().pipe(
      mergeMap(id => {
        return this.breakService.getBreak(id, breakId);
      }),
      mergeMap(data => {
        ctx.setState(
          patch<BreakStateModel>({
            currentBreak: data,
          }),
        );
        return ctx.dispatch(new GetBreakSuccess());
      }),
      catchError(err => ctx.dispatch(new GetBreakFailure(err))),
    );
  }

  @Action([GetBreakSuccess, GetBreakFailure])
  getBreakEnded(ctx: StateContext<BreakStateModel>) {
    ctx.patchState({ isFetchingBreak: false });
  }

  @Action(CreateBreakRequest)
  createBreak(ctx: StateContext<BreakStateModel>, { breakObj }: CreateBreakRequest) {
    return this.getCurrentRadioId().pipe(
      mergeMap(id => this.breakService.addBreak(id, breakObj)),
      mergeMap(() => ctx.dispatch(new CreateBreakSuccess(breakObj))),
      catchError(err => ctx.dispatch(new CreateBreakFailure(err))),
    );
  }

  @Action([
    CreateBreakSuccess,
    EditBreakSuccess,
    DeleteBreakSuccess,
    DeleteSelectedBreaksSuccess,
  ])
  updateBreaksSuccess(ctx: StateContext<BreakStateModel>) {
    if (!ctx.getState().breaks?.breaks?.length) {
      // No break before, so its creation
      this.brevoService.trackEvent(BREVO_EVENT.BREAK_CREATED);
    }
    ctx.dispatch(new GetBreakListRequest(ctx.getState().currentDatesRange));
  }

  @Action(EditBreakRequest)
  editBreak(ctx: StateContext<BreakStateModel>, { breakObj }: EditBreakRequest) {
    return this.getCurrentRadioId().pipe(
      mergeMap(id => this.breakService.editBreak(id, breakObj)),
      mergeMap(() => ctx.dispatch(new EditBreakSuccess())),
      catchError(err => ctx.dispatch(new EditBreakFailure(err))),
    );
  }

  @Action(DeleteBreakRequest)
  deleteBreak(
    ctx: StateContext<BreakStateModel>,
    { deleteAll, breakId, occurrenceId }: DeleteBreakRequest,
  ) {
    ctx.patchState({ isDeletingBreak: true });
    return this.getCurrentRadioId().pipe(
      mergeMap(id => {
        return this.breakService.deleteBreak(
          id,
          breakId,
          !deleteAll ? occurrenceId : null,
        );
      }),
      mergeMap(() => {
        return ctx.dispatch(new DeleteBreakSuccess());
      }),
      catchError(err => ctx.dispatch(new DeleteBreakFailure(err))),
    );
  }

  @Action(DeleteSelectedBreaksRequest)
  deleteSelectedBreaks(ctx: StateContext<BreakStateModel>) {
    ctx.patchState({ isDeletingBreak: true });
    const selected = ctx.getState().selectedBreaks;

    return this.getCurrentRadioId().pipe(
      mergeMap(id => {
        return this.breakService.deleteSelectedBreaks(id, selected);
      }),
      mergeMap(() => {
        return ctx.dispatch(new DeleteSelectedBreaksSuccess());
      }),
      catchError(err => ctx.dispatch(new DeleteSelectedBreaksFailure(err))),
    );
  }

  @Action([
    DeleteBreakSuccess,
    DeleteBreakFailure,
    DeleteSelectedBreaksSuccess,
    DeleteSelectedBreaksFailure,
  ])
  deleteBreakDone(ctx: StateContext<BreakStateModel>) {
    ctx.patchState({ isDeletingBreak: false });
  }

  @Action(SelectBreaks)
  selectBreak(ctx: StateContext<BreakStateModel>, { selectedBreaks }: SelectBreaks) {
    ctx.patchState({ selectedBreaks });
  }

  @Action(ResetBreak)
  resetBreak(ctx: StateContext<BreakStateModel>) {
    ctx.patchState({ currentBreak: null });
  }

  @Action(BreaksRendered)
  breaksRendered(ctx: StateContext<BreakStateModel>) {
    ctx.patchState({ isRenderingBreakList: false });
  }

  @Action(ResetBreakState)
  resetState(ctx: StateContext<BreakStateModel>) {
    ctx.patchState(defaultState);
  }

  @Action(SetCurrentDay)
  setCurrentDay(ctx: StateContext<BreakStateModel>, { currentDay }: SetCurrentDay) {
    const cloneCurrent = currentDay
      .clone()
      .tz(this.store.selectSnapshot(RadioState.currentRadioTimezone), true);
    const currentDatesRange: DatesRange = {
      from: cloneCurrent.clone().startOf('week'),
      to: cloneCurrent.clone().endOf('week'),
    };
    const prevRange = ctx.getState().currentDatesRange.from;
    ctx.patchState({ currentDay: cloneCurrent, currentDatesRange });
    if (prevRange && !prevRange.isSame(currentDatesRange.from)) {
      ctx.dispatch(new GetBreakListRequest(currentDatesRange));
    }
  }

  getCurrentRadioId(): Observable<number> {
    return this.store.select(RadioState.currentRadioId).pipe(
      filter(data => !!data),
      take(1),
    );
  }

  getCurrentRadioTimezone(): Observable<string> {
    return this.store.select(RadioState.currentRadioTimezone).pipe(
      filter(data => !!data),
      take(1),
    );
  }
}
